import { useCallback, useMemo, useState } from "react";
import { Form, useToast, type FormInstance } from "@ir/ui";
import {
  CreateMasterDataRawRetailerAssociationDTO,
  UpdateMasterDataRawRetailerAssociationDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataRawRetailerAssociation } from "@src/domain/entities/masterData";
import {
  CreateMasterDataRawRetailerAssociationUseCase,
  UpdateMasterDataRawRetailerAssociationUseCase,
} from "@src/application/useCases/masterData";
import { MasterDataRawRetailerAssociationRepository } from "@src/infrastructure/repositories/masterData";
import {
  EMPTY_RAW_ASSOC_FORM_VALUES,
  type RawRetailerAssociationFormMode,
  type RawRetailerAssociationFormValues,
} from "./MasterDataRawRetailerAssociationAddEditForm";
import { getMasterDataSaveErrorMessage } from "./masterDataSaveErrors";
import {
  isRawRetailerNameDuplicateError,
  RAW_RETAILER_NAME_ALREADY_EXISTS_MESSAGE,
} from "./retailerChildValidation";

interface UseMasterDataRawRetailerAssociationFormOptions {
  onSaved: () => void;
  lockedRetailerId: number | null;
  lockedRetailerName: string;
}

export function useMasterDataRawRetailerAssociationForm({
  onSaved,
  lockedRetailerId,
  lockedRetailerName,
}: UseMasterDataRawRetailerAssociationFormOptions) {
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<RawRetailerAssociationFormValues>();
  const [formMode, setFormMode] =
    useState<RawRetailerAssociationFormMode | null>(null);
  const [editingEntity, setEditingEntity] =
    useState<MasterDataRawRetailerAssociation | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);

  const repository = useMemo(
    () => new MasterDataRawRetailerAssociationRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataRawRetailerAssociationUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataRawRetailerAssociationUseCase(repository),
    [repository],
  );

  const initialValues = useMemo((): RawRetailerAssociationFormValues => {
    if (formMode === "edit" && editingEntity) {
      return { rawRetailerName: editingEntity.rawRetailerName };
    }
    return EMPTY_RAW_ASSOC_FORM_VALUES;
  }, [editingEntity, formMode]);

  const closeForm = useCallback(
    (formInstance?: FormInstance<RawRetailerAssociationFormValues>) => {
      setFormMode(null);
      setEditingEntity(null);
      setIsFormSubmittable(false);
      (formInstance ?? form).resetFields();
    },
    [form],
  );

  const openCreate = useCallback(() => {
    setEditingEntity(null);
    setFormMode("create");
    setIsFormSubmittable(false);
  }, []);

  const openEdit = useCallback((entity: MasterDataRawRetailerAssociation) => {
    setEditingEntity(entity);
    setFormMode("edit");
    setIsFormSubmittable(false);
  }, []);

  const handleSubmit = useCallback(
    async (values: RawRetailerAssociationFormValues) => {
      setIsSaving(true);
      try {
        const rawRetailerName = values.rawRetailerName.trim();

        if (formMode === "edit" && editingEntity) {
          await updateUseCase.execute(
            new UpdateMasterDataRawRetailerAssociationDTO({
              rawRetailerId: editingEntity.rawRetailerId,
              rawRetailerName,
            }),
          );
        } else {
          if (lockedRetailerId == null || lockedRetailerId <= 0) {
            errorToast({ content: "Retailer is required" });
            return;
          }
          await createUseCase.execute(
            new CreateMasterDataRawRetailerAssociationDTO({
              retailerId: lockedRetailerId,
              rawRetailerName,
            }),
          );
        }

        successToast({
          content: "Raw retailer association saved successfully",
        });
        closeForm();
        onSaved();
      } catch (err) {
        if (isRawRetailerNameDuplicateError(err)) {
          form.setFields([
            {
              name: "rawRetailerName",
              errors: [RAW_RETAILER_NAME_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getMasterDataSaveErrorMessage(
              err,
              "Failed to save raw retailer association. Please try again.",
              "Failed to save raw retailer association. Please try again later.",
            ),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeForm,
      createUseCase,
      editingEntity,
      errorToast,
      form,
      formMode,
      lockedRetailerId,
      onSaved,
      successToast,
      updateUseCase,
    ],
  );

  return {
    form,
    formMode,
    isSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    initialValues,
    lockedRetailerName,
    openCreate,
    openEdit,
    closeForm,
    handleSubmit,
  };
}
